"use strict";
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.default = exports.BingMap = void 0;
var _checkoutUtils = require("@ori/checkout-utils");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _constants = require("../../constants");
var _constantsGenerated = require("../../constants-generated");
var _SearchConfigurationProvider = require("../../containers/SearchConfigurationProvider");
var _contexts = require("../../contexts");
var _logger = require("../../logger");
var _models = require("../../models");
var _Loading = require("../Loading");
var _shared = require("../shared");
var _jsxRuntime = require("react/jsx-runtime");
const BingMap = ()=>{
    const { getTestIdProps } = (0, _testidGenerator.default)();
    const { credentials, minZoom, setMapMethods, viewSettings, setViewSettings } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
    const { clusters } = (0, _contexts.usePickupPoints)();
    const { currentLocation, setSelectedDelivery } = (0, _contexts.useMap)();
    const [isMapLoading, setIsMapLoading] = (0, _react.useState)(false);
    const map = (0, _react.useRef)(null);
    const mapContainer = (0, _react.useRef)(null);
    const updatePushpins = (0, _react.useCallback)(async ()=>{
        if (!viewSettings || !map.current) {
            return;
        }
        // Clean old pins
        map.current.entities.clear();
        // Render clusters and points
        clusters.forEach((param)=>{
            let { isSelected, latitude, longitude, ...entry } = param;
            var _map_current;
            const { x, y } = (0, _shared.getAnchorCoordinates)(entry.type === 'point', isSelected);
            const item = new Microsoft.Maps.Pushpin(new Microsoft.Maps.Location(latitude, longitude), {
                anchor: new Microsoft.Maps.Point(x, y),
                icon: entry.type === 'point' ? (0, _shared.getPickupPointMapIconSvgString)(entry.deliveryMethod, isSelected) : (0, _shared.getClusterMapIconSvgString)(entry.displaySize, isSelected)
            });
            Microsoft.Maps.Events.addHandler(item, 'click', entry.type === 'point' ? ()=>setSelectedDelivery(entry.deliveryChannelId, {
                    latitude,
                    longitude
                }, viewSettings.zoom) : ()=>{
                var _map_current;
                return (_map_current = map.current) === null || _map_current === void 0 ? void 0 : _map_current.setView({
                    bounds: Microsoft.Maps.LocationRect.fromEdges(entry.north, entry.west, entry.south, entry.east),
                    padding: Math.round(_shared.baseMarkerSize / 2)
                });
            });
            (_map_current = map.current) === null || _map_current === void 0 ? void 0 : _map_current.entities.push(item);
        });
        // Render current location pin
        if (currentLocation) {
            const { x, y } = (0, _shared.getAnchorCoordinates)(false, false);
            const anchor = new Microsoft.Maps.Point(x, y);
            const { latitude, longitude } = currentLocation;
            const item = new Microsoft.Maps.Pushpin(new Microsoft.Maps.Location(latitude, longitude), {
                anchor,
                icon: (0, _shared.getCurrentLocationIconSvgString)()
            });
            map.current.entities.push(item);
        }
    }, [
        viewSettings,
        clusters,
        currentLocation,
        setSelectedDelivery
    ]);
    const updateMap = (0, _react.useCallback)(async ()=>{
        setIsMapLoading(false);
        if (!viewSettings || !mapContainer.current || !credentials) {
            return;
        }
        // Initialize map instance
        if (!map.current) {
            const bingMap = new Microsoft.Maps.Map(mapContainer.current, {
                credentials,
                enableClickableLogo: false,
                minZoom,
                maxZoom: _constants.MAP_MAX_ZOOM,
                showDashboard: false,
                showLocateMeButton: false,
                showLogo: false,
                showMapTypeSelector: false,
                showScalebar: false,
                showTermsLink: false,
                mapTypeId: Microsoft.Maps.MapTypeId.road,
                // Enforce tile-cached layer
                disableBirdseye: true // Avoid triggering 3D/oblique requests
            });
            map.current = bingMap;
            // Deselect last selection
            Microsoft.Maps.Events.addHandler(bingMap, 'click', ()=>setSelectedDelivery());
            // Update map view settings
            Microsoft.Maps.Events.addHandler(bingMap, 'viewchangeend', ()=>{
                const location = bingMap.getCenter();
                const zoom = bingMap.getZoom();
                setViewSettings((p)=>zoom === p.zoom && location.latitude === p.location.latitude && location.longitude === p.location.longitude ? p : {
                        location,
                        zoom
                    });
            });
            // TODO: Remove map methods after flat selection UI is used in all markets
            const getViewSettings = (viewOptions)=>{
                var _viewOptions_location, _viewOptions_zoom;
                return {
                    location: (_viewOptions_location = viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.location) !== null && _viewOptions_location !== void 0 ? _viewOptions_location : bingMap.getCenter(),
                    zoom: (_viewOptions_zoom = viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.zoom) !== null && _viewOptions_zoom !== void 0 ? _viewOptions_zoom : bingMap.getZoom()
                };
            };
            // TODO: Remove map methods after flat selection UI is used in all markets
            // Expose the generic map methods
            setMapMethods({
                getViewSettings,
                setLocationOrZoom: (viewOptions)=>{
                    const { location, zoom } = getViewSettings(viewOptions);
                    bingMap.setView({
                        center: new Microsoft.Maps.Location(location.latitude, location.longitude),
                        zoom
                    });
                    setViewSettings({
                        location,
                        zoom
                    });
                },
                getViewBounds: ()=>{
                    const bounds = bingMap.getBounds();
                    return {
                        east: bounds.getEast(),
                        north: bounds.getNorth(),
                        south: bounds.getSouth(),
                        west: bounds.getWest()
                    };
                },
                setViewBounds: (param, padding)=>{
                    let { east, north, south, west } = param;
                    return bingMap.setView({
                        bounds: Microsoft.Maps.LocationRect.fromEdges(north, west, south, east),
                        padding
                    });
                }
            });
        }
        // Update map view to match view settings
        map.current.setView({
            center: new Microsoft.Maps.Location(viewSettings.location.latitude, viewSettings.location.longitude),
            mapTypeId: Microsoft.Maps.MapTypeId.road,
            zoom: viewSettings.zoom
        });
        await updatePushpins();
    }, [
        viewSettings,
        credentials,
        minZoom,
        setSelectedDelivery,
        setMapMethods,
        updatePushpins,
        setViewSettings
    ]);
    // Load map script
    (0, _react.useEffect)(()=>{
        var _window_Microsoft;
        if (isMapLoading) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- wrong 3rd party typings
        if (((_window_Microsoft = window.Microsoft) === null || _window_Microsoft === void 0 ? void 0 : _window_Microsoft.Maps) !== undefined) {
            void updateMap();
        } else {
            // Set callback function to be called when the script is loaded
            window.updateMap = updateMap;
            setIsMapLoading(true);
            (0, _checkoutUtils.loadScript)("https://www.bing.com/api/maps/mapcontrol?callback=updateMap", 'bing-map-loader-script', _constantsGenerated.APP_NAME).catch((error)=>{
                (0, _checkoutUtils.showErrorNotification)();
                (0, _logger.logWarning)('Bing map script failed to load. Map will not be displayed.', error);
                setIsMapLoading(false);
            });
        }
    }, [
        isMapLoading,
        updateMap
    ]);
    return /*#__PURE__*/ (0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [
            isMapLoading ? /*#__PURE__*/ (0, _jsxRuntime.jsx)(_Loading.MapLoadingOverlay, {}) : null,
            /*#__PURE__*/ (0, _jsxRuntime.jsx)("div", {
                ref: mapContainer,
                ...getTestIdProps(_models.TestId.BingMapContainer)
            })
        ]
    });
};
exports.BingMap = BingMap;
var _default = exports.default = BingMap;
